import type { PairMetrics, PhotoRecord } from "./types";
import { decodeImageBitmap } from "./image-decode";

const HASH_WIDTH = 17;
const HASH_HEIGHT = 16;
const VERIFY_SIZE = 96;

async function bitmapFor(file: File): Promise<ImageBitmap> {
  return decodeImageBitmap(file);
}

function canvasPixels(bitmap: ImageBitmap, width: number, height: number): Uint8ClampedArray {
  const canvas = new OffscreenCanvas(width, height);
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Canvas is unavailable in this browser.");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(bitmap, 0, 0, width, height);
  return context.getImageData(0, 0, width, height).data;
}

function grayscale(data: Uint8ClampedArray): Float64Array {
  const result = new Float64Array(data.length / 4);
  for (let source = 0, target = 0; source < data.length; source += 4, target += 1) {
    result[target] = data[source] * 0.299 + data[source + 1] * 0.587 + data[source + 2] * 0.114;
  }
  return result;
}

export async function exactHash(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function inspectImage(file: File): Promise<{
  width: number;
  height: number;
  visualHash: string;
  sharpness: number;
}> {
  const bitmap = await bitmapFor(file);
  try {
    const pixels = canvasPixels(bitmap, HASH_WIDTH, HASH_HEIGHT);
    const gray = grayscale(pixels);
    const bytes = new Uint8Array((HASH_WIDTH - 1) * HASH_HEIGHT / 8);
    let bit = 0;
    let edgeEnergy = 0;

    for (let y = 0; y < HASH_HEIGHT; y += 1) {
      for (let x = 0; x < HASH_WIDTH - 1; x += 1) {
        const current = gray[y * HASH_WIDTH + x];
        const next = gray[y * HASH_WIDTH + x + 1];
        if (current > next) bytes[Math.floor(bit / 8)] |= 1 << (7 - (bit % 8));
        edgeEnergy += Math.abs(current - next);
        bit += 1;
      }
    }

    return {
      width: bitmap.width,
      height: bitmap.height,
      visualHash: [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join(""),
      sharpness: edgeEnergy / bit,
    };
  } finally {
    bitmap.close();
  }
}

export async function decodedPixelHash(file: File): Promise<string> {
  const bitmap = await bitmapFor(file);
  try {
    const decodedPixels = canvasPixels(bitmap, bitmap.width, bitmap.height);
    const pixelBytes = Uint8Array.from(decodedPixels);
    const digest = await crypto.subtle.digest("SHA-256", pixelBytes.buffer);
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  } finally {
    bitmap.close();
  }
}

export function hammingDistance(left: string, right: string): number {
  let distance = 0;
  for (let index = 0; index < Math.min(left.length, right.length); index += 1) {
    let value = Number.parseInt(left[index], 16) ^ Number.parseInt(right[index], 16);
    while (value) {
      distance += value & 1;
      value >>>= 1;
    }
  }
  return distance + Math.abs(left.length - right.length) * 4;
}

function ssim(left: Float64Array, right: Float64Array): number {
  const length = Math.min(left.length, right.length);
  let leftMean = 0;
  let rightMean = 0;
  for (let index = 0; index < length; index += 1) {
    leftMean += left[index];
    rightMean += right[index];
  }
  leftMean /= length;
  rightMean /= length;

  let leftVariance = 0;
  let rightVariance = 0;
  let covariance = 0;
  for (let index = 0; index < length; index += 1) {
    const leftDelta = left[index] - leftMean;
    const rightDelta = right[index] - rightMean;
    leftVariance += leftDelta * leftDelta;
    rightVariance += rightDelta * rightDelta;
    covariance += leftDelta * rightDelta;
  }
  const denominator = Math.max(1, length - 1);
  leftVariance /= denominator;
  rightVariance /= denominator;
  covariance /= denominator;
  const c1 = (0.01 * 255) ** 2;
  const c2 = (0.03 * 255) ** 2;
  return ((2 * leftMean * rightMean + c1) * (2 * covariance + c2)) /
    ((leftMean ** 2 + rightMean ** 2 + c1) * (leftVariance + rightVariance + c2));
}

export async function compareImages(left: PhotoRecord, right: PhotoRecord): Promise<PairMetrics> {
  const [leftBitmap, rightBitmap] = await Promise.all([bitmapFor(left.file), bitmapFor(right.file)]);
  try {
    const leftGray = grayscale(canvasPixels(leftBitmap, VERIFY_SIZE, VERIFY_SIZE));
    const rightGray = grayscale(canvasPixels(rightBitmap, VERIFY_SIZE, VERIFY_SIZE));
    let absoluteError = 0;
    let squaredError = 0;
    for (let index = 0; index < leftGray.length; index += 1) {
      const delta = leftGray[index] - rightGray[index];
      absoluteError += Math.abs(delta);
      squaredError += delta * delta;
    }
    const mae = absoluteError / leftGray.length;
    const mse = squaredError / leftGray.length;
    return {
      hamming: hammingDistance(left.visualHash ?? "", right.visualHash ?? ""),
      mae,
      psnr: mse === 0 ? Number.POSITIVE_INFINITY : 10 * Math.log10((255 * 255) / mse),
      ssim: ssim(leftGray, rightGray),
    };
  } finally {
    leftBitmap.close();
    rightBitmap.close();
  }
}

async function thumbnailBlob(photo: PhotoRecord, max = 720): Promise<Blob> {
  return thumbnailBlobForFile(photo.file, max);
}

export async function thumbnailBlobForFile(file: File, max = 720): Promise<Blob> {
  const bitmap = await bitmapFor(file);
  try {
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas is unavailable.");
    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.convertToBlob({ type: "image/webp", quality: 0.78 });
  } finally {
    bitmap.close();
  }
}

export async function thumbnailUrl(photo: PhotoRecord): Promise<string> {
  return URL.createObjectURL(await thumbnailBlob(photo));
}

export async function thumbnailDataUrl(photo: PhotoRecord, max = 360): Promise<string> {
  const blob = await thumbnailBlob(photo, max);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Unable to encode report thumbnail."));
    reader.readAsDataURL(blob);
  });
}
